/* Independent PDP enhancements. Native links/forms remain the source of truth. */
(() => {
  if (customElements.get('pdp-details')) return;
  class PDPDetails extends HTMLElement {
    connectedCallback() {
      this.cleanup?.();
      let frame = 0, scrolled = false, headerObserver;
      const update = () => {
        frame = 0;
        const header = document.querySelector('.header[data-header-state-ready]');
        // The page header owns this state. Isolated/native no-header contexts
        // retain the measured 80px entry and reset only at the document origin.
        if (header) scrolled = header.hasAttribute('data-header-scrolled');
        else if (window.scrollY <= 0) scrolled = false;
        else if (window.scrollY >= 80) scrolled = true;
        this.toggleAttribute('data-pdp-scrolled', scrolled);
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      const observeHeader = () => {
        headerObserver?.disconnect();
        const header = document.querySelector('.header');
        if (header) {
          headerObserver = new MutationObserver(schedule);
          headerObserver.observe(header, { attributes: true, attributeFilter: ['data-header-state-ready', 'data-header-scrolled'] });
        }
        schedule();
      };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      document.addEventListener('shopify:section:load', observeHeader);
      observeHeader(); update();
      this.cleanup = () => {
        cancelAnimationFrame(frame); headerObserver?.disconnect();
        window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule);
        document.removeEventListener('shopify:section:load', observeHeader);
      };
    }
    disconnectedCallback() { this.cleanup?.(); }
  }
  class PDPQuantity extends HTMLElement {
    connectedCallback() {
      this.cleanup?.();
      const input = this.querySelector('[data-quantity-input]');
      if (!input) return;
      const root = this.closest('[data-product-root]') || this;
      let rules = {};
      try { rules = JSON.parse(this.querySelector('[data-quantity-rules]')?.textContent || '{}'); } catch {}
      const sync = (normalize = false) => {
        const min = Number(input.min || 1), step = Number(input.step || 1);
        const max = input.max === '' ? Infinity : Number(input.max);
        let value = Number(input.value);
        if (normalize) { value = Math.max(min, Math.min(max, Number.isFinite(value) ? value : min)); value = min + Math.floor((value - min) / step) * step; input.value = String(value); }
        this.querySelector('[data-quantity-change="-1"]').disabled = value <= min;
        this.querySelector('[data-quantity-change="1"]').disabled = value + step > max;
      };
      const variant = () => {
        const rule = rules[root.querySelector('[data-variant-id-input]')?.value];
        if (rule) { input.min = rule.min || 1; input.step = rule.increment || 1; if (rule.max != null) input.max = rule.max; else input.removeAttribute('max'); }
        sync(true);
      };
      const click = event => {
        const button = event.target.closest('[data-quantity-change]');
        if (!button || !this.contains(button)) return;
        event.stopPropagation(); // Own this control once upgraded; avoid the shared cart increment handler.
        if (button.disabled) return;
        input.value = String(Number(input.value || input.min || 1) + Number(button.dataset.quantityChange) * Number(input.step || 1));
        sync(true); input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      const change = event => { if (event.target === input) sync(true); else queueMicrotask(variant); };
      this.addEventListener('click', click);
      root.addEventListener('change', change);
      sync(true);
      this.cleanup = () => { this.removeEventListener('click', click); root.removeEventListener('change', change); };
    }
    disconnectedCallback() { this.cleanup?.(); }
  }
  class PDPRecommendations extends HTMLElement {
    connectedCallback() {
      this.cleanup?.();
      const track = this.querySelector('[data-pdp-rec-track]');
      const controls = this.querySelector('[data-pdp-rec-controls]');
      if (!track || !controls) return;
      const previous = controls.querySelector('[data-pdp-rec-prev]');
      const next = controls.querySelector('[data-pdp-rec-next]');
      const sync = () => {
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        controls.hidden = max < 2;
        previous.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft >= max - 1;
      };
      const click = event => {
        const button = event.target.closest('[data-pdp-rec-prev],[data-pdp-rec-next]');
        if (!button || button.disabled) return;
        const first = track.firstElementChild;
        const step = (first?.getBoundingClientRect().width || track.clientWidth) + parseFloat(getComputedStyle(track).columnGap || 0);
        track.scrollBy({ left: button === previous ? -step : step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      };
      const observer = new ResizeObserver(sync);
      observer.observe(track);
      this.addEventListener('click', click);
      track.addEventListener('scroll', sync, { passive: true });
      this.addEventListener('load', sync, true);
      sync();
      this.cleanup = () => { observer.disconnect(); this.removeEventListener('click', click); track.removeEventListener('scroll', sync); this.removeEventListener('load', sync, true); };
    }
    disconnectedCallback() { this.cleanup?.(); }
  }
  class PDPVideoFeedback extends HTMLElement {
    connectedCallback() {
      this.cleanup?.();
      const dialog = this.querySelector('dialog');
      const modal = this.querySelector('dialog-component');
      const viewport = this.querySelector('[data-pdp-video-viewport]');
      const track = this.querySelector('[data-pdp-video-track]');
      if (!dialog || !viewport || !track) return;
      const slides = [...this.querySelectorAll('[data-pdp-video-slide]')];
      const thumbnails = [...this.querySelectorAll('[data-pdp-video-select]')];
      const previous = this.querySelector('[data-pdp-video-prev]');
      const next = this.querySelector('[data-pdp-video-next]');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      let current = 0, opener = null, closingTimer = 0, frame = 0, drag = null, suppressClick = false, alive = true;
      const ownDialog = target => target.closest('dialog') === dialog;
      // Playback is scoped to the dialog; trigger thumbnails are static Shopify
      // preview images and never start a separate media request.
      const pauseAll = () => dialog.querySelectorAll('video').forEach(video => video.pause());
      const sync = slide => {
        const video = slide.querySelector('video');
        const play = slide.querySelector('[data-pdp-video-play]');
        const mute = slide.querySelector('[data-pdp-video-mute]');
        if (!video || !play || !mute) return;
        play.setAttribute('aria-pressed', String(!video.paused));
        play.setAttribute('aria-label', video.paused ? play.dataset.playLabel : play.dataset.pauseLabel);
        play.querySelector('[data-pdp-video-play-icon]').hidden = !video.paused;
        play.querySelector('[data-pdp-video-pause-icon]').hidden = video.paused;
        mute.setAttribute('aria-pressed', String(video.muted));
        mute.setAttribute('aria-label', video.muted ? mute.dataset.mutedLabel : mute.dataset.unmutedLabel);
        mute.querySelector('[data-pdp-video-muted-icon]').toggleAttribute('hidden', !video.muted);
        mute.querySelector('[data-pdp-video-unmuted-icon]').toggleAttribute('hidden', video.muted);
      };
      const activeVideo = video => alive && dialog.open && !document.hidden && video === slides[current]?.querySelector('video');
      const playVideo = video => {
        if (!video || !activeVideo(video)) return;
        const result = video.play();
        result?.then(() => { if (!activeVideo(video)) video.pause(); }).catch(() => sync(video.closest('[data-pdp-video-slide]')));
      };
      const position = (offset = 0) => { track.style.transform = `translateX(calc(${-100 * current}% - ${8 * current}px + ${offset}px))`; };
      const select = (index, play = true, load = true, instant = false) => {
        if (!slides.length) return;
        current = Math.max(0, Math.min(slides.length - 1, index));
        pauseAll();
        slides.forEach((slide, i) => { slide.inert = i !== current; slide.setAttribute('aria-hidden', String(i !== current)); slide.removeAttribute('data-controls-visible'); });
        const slide = slides[current];
        const template = slide.querySelector('[data-pdp-video-template]');
        if (load && !slide.querySelector('video') && template) slide.querySelector('[data-pdp-video-media]').append(template.content.cloneNode(true));
        const video = slide.querySelector('video');
        if (video) {
          video.playsInline = true;
          video.loop = true;
          try { video.currentTime = 0; } catch {}
          if (!video.dataset.feedbackLoaded) {
            video.muted = false;
            video.volume = 1;
            video.dataset.feedbackLoaded = 'true';
          }
        }
        thumbnails.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.pdpVideoSelect === slide.dataset.pdpVideoSlide)));
        previous.disabled = current === 0; next.disabled = current === slides.length - 1;
        previous.hidden = next.hidden = slides.length < 2;
        cancelAnimationFrame(frame);
        track.toggleAttribute('data-instant', instant);
        position();
        if (instant) frame = requestAnimationFrame(() => { frame = requestAnimationFrame(() => track.removeAttribute('data-instant')); });
        sync(slide);
        if (play) playVideo(video);
      };
      const open = button => {
        clearTimeout(closingTimer); closingTimer = 0; dialog.removeAttribute('data-pdp-closing');
        opener = button;
        select(slides.findIndex(slide => slide.dataset.pdpVideoSlide === button.dataset.pdpVideoOpen), false, true, true);
        if (modal?.showDialog) modal.showDialog(button); else if (!dialog.open) dialog.showModal();
        playVideo(slides[current]?.querySelector('video'));
      };
      const finishClose = () => {
        pauseAll();
        clearTimeout(closingTimer); closingTimer = 0;
        if (modal?.close) modal.close(); else if (dialog.open) dialog.close();
        dialog.removeAttribute('data-pdp-closing');
      };
      const close = (immediate = false) => {
        if (immediate || reduced.matches) { finishClose(); return; }
        if (!dialog.open || closingTimer) return;
        dialog.setAttribute('data-pdp-closing', '');
        closingTimer = setTimeout(finishClose, 500);
      };
      const closed = () => { if (!dialog.open) { pauseAll(); clearTimeout(closingTimer); closingTimer = 0; dialog.removeAttribute('data-pdp-closing'); opener?.isConnected && opener.focus({ preventScroll: true }); opener = null; } };
      const click = event => {
        if (event.target.closest('pdp-video-feedback') !== this) return;
        const trigger = event.target.closest('[data-pdp-video-open]');
        if (trigger) {
          // A drag that scrolled the row must not also open a video.
          if (suppressClick) { suppressClick = false; event.preventDefault(); event.stopPropagation(); return; }
          open(trigger);
          return;
        }
        if (!ownDialog(event.target)) return;
        if (suppressClick) { suppressClick = false; event.preventDefault(); event.stopPropagation(); return; }
        if (event.target.closest('[data-dialog-close]') || event.target === dialog) { event.preventDefault(); event.stopPropagation(); close(); return; }
        if (closingTimer) return;
        const thumb = event.target.closest('[data-pdp-video-select]');
        if (thumb) { select(slides.findIndex(slide => slide.dataset.pdpVideoSlide === thumb.dataset.pdpVideoSelect)); return; }
        if (event.target.closest('[data-pdp-video-prev]')) { if (!previous.disabled) select(current - 1); return; }
        if (event.target.closest('[data-pdp-video-next]')) { if (!next.disabled) select(current + 1); return; }
        const slide = slides[current], video = slide?.querySelector('video');
        if (!video) return;
        if (event.target.closest('[data-pdp-video-play]')) { if (video.paused) playVideo(video); else video.pause(); sync(slide); }
        else if (event.target.closest('[data-pdp-video-mute]')) { video.muted = !video.muted; sync(slide); }
        else if (event.target.closest('[data-pdp-video-media]')) slide.toggleAttribute('data-controls-visible');
      };
      const key = event => {
        if (!dialog.open || closingTimer || !ownDialog(event.target) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.target.closest('input,textarea,select,[contenteditable="true"]') || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
        event.preventDefault(); select(current + (event.key === 'ArrowLeft' ? -1 : 1));
      };
      const cancel = event => { if (event.target === dialog) { event.preventDefault(); close(); } };
      const mediaState = event => {
        const slide = event.target.closest?.('[data-pdp-video-slide]');
        if (!slide || slide.closest('pdp-video-feedback') !== this) return;
        if (event.type === 'play' && !activeVideo(event.target)) event.target.pause();
        sync(slide);
      };
      const down = event => {
        suppressClick = false;
        if (!dialog.open || closingTimer || !event.isPrimary || event.button !== 0 || event.target.closest('a,button,input,select,textarea')) return;
        drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, moved: false };
      };
      const move = event => {
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (!drag.moved && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { drag = null; return; }
        if (!drag.moved && Math.abs(dx) > 8) { drag.moved = true; viewport.setPointerCapture(event.pointerId); track.setAttribute('data-dragging', ''); }
        if (!drag.moved) return;
        event.preventDefault(); drag.dx = dx;
        const bounded = (current === 0 && dx > 0) || (current === slides.length - 1 && dx < 0);
        position(bounded ? dx / 4 : dx);
      };
      const end = event => {
        if (!drag || drag.id !== event.pointerId) return;
        const { dx, moved, id } = drag; drag = null;
        if (viewport.hasPointerCapture(id)) viewport.releasePointerCapture(id);
        track.removeAttribute('data-dragging');
        if (moved) { suppressClick = event.type !== 'pointercancel'; if (event.type !== 'pointercancel' && Math.abs(dx) > Math.min(60, viewport.clientWidth * .18)) select(current + (dx < 0 ? 1 : -1)); else position(); }
      };
      // Drag-to-scroll on the trigger row. The row already scrolls with a wheel
      // or a touch swipe; this adds the same gesture for a mouse, which is the
      // only way to reach later portraits on a desktop without a trackpad. A
      // drag that actually moved suppresses the click so dragging past a circle
      // never opens its video.
      const strip = this.querySelector('.pdp-video-feedback__triggers');
      let stripDrag = null;
      const stripDown = event => {
        if (!strip || !event.isPrimary || event.button !== 0) return;
        if (strip.scrollWidth <= strip.clientWidth) return;
        stripDrag = { id: event.pointerId, x: event.clientX, left: strip.scrollLeft, moved: false };
      };
      const stripMove = event => {
        if (!stripDrag || stripDrag.id !== event.pointerId) return;
        const dx = event.clientX - stripDrag.x;
        if (!stripDrag.moved) {
          if (Math.abs(dx) < 6) return;
          stripDrag.moved = true;
          strip.setAttribute('data-dragging', '');
          strip.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        strip.scrollLeft = stripDrag.left - dx;
      };
      const stripEnd = event => {
        if (!stripDrag || stripDrag.id !== event.pointerId) return;
        const { moved, id } = stripDrag; stripDrag = null;
        if (strip.hasPointerCapture(id)) strip.releasePointerCapture(id);
        strip.removeAttribute('data-dragging');
        if (moved) suppressClick = event.type !== 'pointercancel';
      };
      if (strip) {
        strip.addEventListener('pointerdown', stripDown);
        strip.addEventListener('pointermove', stripMove);
        strip.addEventListener('pointerup', stripEnd);
        strip.addEventListener('pointercancel', stripEnd);
        strip.addEventListener('dragstart', preventDrag);
      }

      const hide = () => {
        if (document.hidden) pauseAll();
        else if (dialog.open) playVideo(slides[current]?.querySelector('video'));
      };
      // Capture close gestures before the shared dialog host can close synchronously.
      this.addEventListener('click', click, true); dialog.addEventListener('keydown', key); dialog.addEventListener('close', closed); dialog.addEventListener('cancel', cancel);
      for (const type of ['play','pause','volumechange']) this.addEventListener(type, mediaState, true);
      viewport.addEventListener('pointerdown', down); viewport.addEventListener('pointermove', move); viewport.addEventListener('pointerup', end); viewport.addEventListener('pointercancel', end);
      viewport.addEventListener('dragstart', preventDrag);
      function preventDrag(event) { event.preventDefault(); }
      document.addEventListener('visibilitychange', hide);
      select(0, false, false, true);
      this.cleanup = () => {
        alive = false; drag = null; cancelAnimationFrame(frame); track.removeAttribute('data-dragging'); close(true);
        this.removeEventListener('click', click, true); dialog.removeEventListener('keydown', key); dialog.removeEventListener('close', closed); dialog.removeEventListener('cancel', cancel);
        for (const type of ['play','pause','volumechange']) this.removeEventListener(type, mediaState, true);
        viewport.removeEventListener('pointerdown', down); viewport.removeEventListener('pointermove', move); viewport.removeEventListener('pointerup', end); viewport.removeEventListener('pointercancel', end); viewport.removeEventListener('dragstart', preventDrag);
        stripDrag = null;
        if (strip) {
          strip.removeAttribute('data-dragging');
          strip.removeEventListener('pointerdown', stripDown); strip.removeEventListener('pointermove', stripMove);
          strip.removeEventListener('pointerup', stripEnd); strip.removeEventListener('pointercancel', stripEnd);
          strip.removeEventListener('dragstart', preventDrag);
        }
        document.removeEventListener('visibilitychange', hide);
      };
    }
    disconnectedCallback() { this.cleanup?.(); }
  }
  if (!customElements.get('pdp-video-feedback')) customElements.define('pdp-video-feedback', PDPVideoFeedback);
  if (!customElements.get('pdp-recommendations')) customElements.define('pdp-recommendations', PDPRecommendations);
  if (!customElements.get('pdp-details')) customElements.define('pdp-details', PDPDetails);
  if (!customElements.get('pdp-quantity')) customElements.define('pdp-quantity', PDPQuantity);
  const selectors = 'pdp-details,pdp-quantity,pdp-recommendations,pdp-video-feedback';
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.('pdp-video-feedback')) event.target.cleanup?.();
    event.target.querySelectorAll(selectors).forEach(element => element.cleanup?.());
  });
  document.addEventListener('shopify:section:load', event => {
    if (event.target.matches?.('pdp-video-feedback')) event.target.connectedCallback();
    event.target.querySelectorAll(selectors).forEach(element => element.connectedCallback());
  });
})();
